/**
 * Clés de query centralisées pour React Query.
 *
 * Convention hiérarchique :
 *   queryKeys.clubs.all()          → ['clubs']           (invalide TOUT ce qui concerne clubs)
 *   queryKeys.clubs.list(params)   → ['clubs', 'list', params]
 *   queryKeys.clubs.detail(id)     → ['clubs', id]
 *
 * Usage invalidation après mutation :
 *   queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all() })
 */
export const queryKeys = {
  me: () => ['me'] as const,
  myStats: () => ['me', 'stats'] as const,
  myHistory: (params?: Record<string, unknown>) => ['me', 'history', params] as const,

  users: {
    profile: (id: number) => ['users', id, 'profile'] as const,
    stats: (id: number) => ['users', id, 'stats'] as const,
    history: (id: number, params?: Record<string, unknown>) => ['users', id, 'history', params] as const,
  },

  organizations: {
    all: () => ['organizations'] as const,
    list: (params?: Record<string, unknown>) => ['organizations', 'list', params] as const,
    detail: (id: number) => ['organizations', id] as const,
  },

  adminStats: () => ['admin', 'stats'] as const,

  clubs: {
    all: () => ['clubs'] as const,
    list: (params?: Record<string, unknown>) => ['clubs', 'list', params] as const,
    detail: (id: number) => ['clubs', id] as const,
    analytics: (id: number) => ['clubs', id, 'analytics'] as const,
    myMembershipRequest: () => ['clubs', 'membership-requests', 'my'] as const,
    pendingClaim: (clubId: number) => ['clubs', clubId, 'claim', 'pending'] as const,
    competitions: (clubId: number) => ['clubs', clubId, 'competitions'] as const,
  },

  ffpjp: {
    licenseCheck: (licenseNumber: string) => ['ffpjp', 'license-check', licenseNumber] as const,
  },

  competitions: {
    all: () => ['competitions'] as const,
    list: (params?: Record<string, unknown>) => ['competitions', 'list', params] as const,
    detail: (id: number) => ['competitions', id] as const,
    affiliation: (compId: number, clubId: number) => ['competitions', compId, 'affiliation', clubId] as const,
  },

  teams: {
    all: () => ['teams'] as const,
    list: (params?: Record<string, unknown>) => ['teams', 'list', params] as const,
    detail: (id: number) => ['teams', id] as const,
    byCompetition: (competitionId: number) => ['teams', 'competition', competitionId] as const,
    myTeam: (competitionId: number, captainId: number) => ['teams', 'mine', competitionId, captainId] as const,
    licenseAvailability: (competitionId: number, licenseNumber: string) =>
      ['teams', 'license-availability', competitionId, licenseNumber] as const,
    nameAvailability: (competitionId: number, name: string) =>
      ['teams', 'name-availability', competitionId, name] as const,
  },

  notifications: {
    count: () => ['notifications', 'count'] as const,
    list: () => ['notifications', 'list'] as const,
  },

  blog: {
    all: () => ['blog'] as const,
    articles: (params?: Record<string, unknown>) => ['blog', 'articles', params] as const,
    articleBySlug: (slug: string) => ['blog', 'articles', slug] as const,
    categories: () => ['blog', 'categories'] as const,
  },
};
